'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { EASE_OUT } from '@/components/livecontact/motion'

type Props = {
  /** Changing this swaps the step. */
  stepKey: string
  /** 1 = moving forward, -1 = going back. Decides which way the steps travel. */
  direction?: number
  children: React.ReactNode
  className?: string
}

/** How far a step slides. Small on purpose — the crossfade carries the change. */
const TRAVEL = 20

const variants = {
  enter: ({ direction, reduced }: { direction: number; reduced: boolean }) => ({
    opacity: 0,
    transform: reduced ? 'translateX(0px)' : `translateX(${direction > 0 ? TRAVEL : -TRAVEL}px)`,
  }),
  center: {
    opacity: 1,
    transform: 'translateX(0px)',
    transition: { duration: 0.24, ease: EASE_OUT },
  },
  exit: ({ direction, reduced }: { direction: number; reduced: boolean }) => ({
    opacity: 0,
    transform: reduced ? 'translateX(0px)' : `translateX(${direction > 0 ? -TRAVEL : TRAVEL}px)`,
    transition: { duration: 0.15, ease: EASE_OUT },
  }),
}

/**
 * Swaps one auth step for the next: the outgoing step fades out, the incoming one fades in
 * behind it, and the wrapper animates from the old height to the new one. Without the height
 * animation the card snaps between sizes, which reads as a page change rather than one
 * surface answering.
 *
 * The measurement is deliberately owned by the ref callback rather than an effect: with
 * `mode="wait"` there are moments when no step is mounted, and an effect that measures
 * whatever ref it last held would read a detached node (height 0) and clip the card shut.
 *
 * The horizontal padding is deliberate too — the wrapper clips vertically, and inputs render
 * their focus ring 4px outside their box, so it needs room not to shave the ring off.
 */
const StepSwap = ({ stepKey, direction = 1, children, className }: Props) => {
  const reduced = Boolean(useReducedMotion())
  const observerRef = useRef<ResizeObserver>()
  const [height, setHeight] = useState<number>()

  const setNode = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    // A null (or zero-height) node means the step is on its way out — keep the last good
    // height so the wrapper animates to the next one instead of collapsing through zero.
    if (!node) return

    const measure = () => {
      const next = node.offsetHeight
      if (next > 0) setHeight(next)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const custom = { direction, reduced }

  return (
    <motion.div
      initial={false}
      animate={{ height: height ?? 'auto' }}
      transition={{ duration: 0.26, ease: EASE_OUT }}
      className={`-mx-2 overflow-hidden px-2 ${className || ''}`}
    >
      <AnimatePresence mode="wait" initial={false} custom={custom}>
        <motion.div
          key={stepKey}
          ref={setNode}
          custom={custom}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}

export default StepSwap
